import React from 'react';
import { format } from 'date-fns';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';


const DataVisualization = ({ reportType, reportData, chartData, stats, loading }) => {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Generating report...</p>
      </div>
    );
  }

  const getStatsConfig = () => {
    switch (reportType) {
      case 'sales':
        return [
          { label: 'Total Revenue', value: `$${stats?.totalRevenue?.toFixed(2) || '0.00'}`, icon: 'DollarSign', color: 'text-green-600' },
          { label: 'Total Transactions', value: stats?.totalTransactions || 0, icon: 'ShoppingCart', color: 'text-blue-600' },
          { label: 'Average Transaction', value: `$${stats?.averageTransaction?.toFixed(2) || '0.00'}`, icon: 'TrendingUp', color: 'text-purple-600' },
          { label: 'Total Items Sold', value: stats?.totalItems || 0, icon: 'Package', color: 'text-orange-600' }
        ];
      case 'attendance':
        return [
          { label: 'Total Visits', value: stats?.totalVisits || 0, icon: 'Users', color: 'text-blue-600' },
          { label: 'Unique Members', value: stats?.uniqueMembers || 0, icon: 'UserCheck', color: 'text-green-600' },
          { label: 'Avg Duration (min)', value: stats?.averageDuration || 0, icon: 'Clock', color: 'text-purple-600' },
          { label: 'Peak Day', value: stats?.peakDay || 'N/A', icon: 'Calendar', color: 'text-orange-600' }
        ];
      case 'revenue':
        return [
          { label: 'Total Revenue', value: `$${stats?.totalRevenue?.toFixed(2) || '0.00'}`, icon: 'DollarSign', color: 'text-green-600' },
          { label: 'Completed', value: `$${stats?.completedRevenue?.toFixed(2) || '0.00'}`, icon: 'CheckCircle', color: 'text-blue-600' },
          { label: 'Pending', value: `$${stats?.pendingRevenue?.toFixed(2) || '0.00'}`, icon: 'Clock', color: 'text-yellow-600' },
          { label: 'Total Records', value: stats?.totalRecords || 0, icon: 'FileText', color: 'text-purple-600' }
        ];
      case 'membership':
        return [
          { label: 'Total Subscriptions', value: stats?.totalSubscriptions || 0, icon: 'CreditCard', color: 'text-blue-600' },
          { label: 'Active', value: stats?.activeSubscriptions || 0, icon: 'CheckCircle', color: 'text-green-600' },
          { label: 'Cancelled', value: stats?.cancelledSubscriptions || 0, icon: 'XCircle', color: 'text-red-600' },
          { label: 'Monthly MRR', value: `$${stats?.monthlyRecurringRevenue?.toFixed(2) || '0.00'}`, icon: 'DollarSign', color: 'text-purple-600' }
        ];
      case 'reconciliation':
        return [
          { label: 'Total Records', value: stats?.totalRecords || 0, icon: 'FileText', color: 'text-blue-600' },
          { label: 'Matched', value: stats?.matched || 0, icon: 'CheckCircle', color: 'text-green-600' },
          { label: 'Unmatched', value: stats?.unmatched || 0, icon: 'AlertCircle', color: 'text-red-600' },
          { label: 'Total Discrepancy', value: `$${stats?.totalDiscrepancy?.toFixed(2) || '0.00'}`, icon: 'DollarSign', color: 'text-orange-600' }
        ];
      default:
        return [];
    }
  };

  const statsConfig = getStatsConfig();

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsConfig?.map((stat, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">{stat?.label}</p>
              <Icon name={stat?.icon} size={20} className={stat?.color} />
            </div>
            <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      {chartData?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={18} />
            Trend Analysis
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            {reportType === 'sales' || reportType === 'revenue' ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(date) => format(new Date(date), 'MMM dd')} />
                <YAxis />
                <Tooltip labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#667eea" strokeWidth={2} name="Revenue" />
                {reportType === 'sales' && <Line type="monotone" dataKey="transactions" stroke="#f59e0b" strokeWidth={2} name="Transactions" />}
              </LineChart>
            ) : (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(date) => format(new Date(date), 'MMM dd')} />
                <YAxis />
                <Tooltip labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')} />
                <Legend />
                {reportType === 'attendance' && (
                  <>
                    <Bar dataKey="visits" fill="#667eea" name="Visits" />
                    <Bar dataKey="uniqueMembers" fill="#10b981" name="Unique Members" />
                  </>
                )}
                {reportType === 'membership' && (
                  <>
                    <Bar dataKey="newSubscriptions" fill="#667eea" name="New Subscriptions" />
                    <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                  </>
                )}
                {reportType === 'reconciliation' && (
                  <>
                    <Bar dataKey="matched" fill="#10b981" name="Matched" />
                    <Bar dataKey="unmatched" fill="#ef4444" name="Unmatched" />
                  </>
                )}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-6 border-b border-border">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Table" size={18} />
            Report Data ({reportData?.length} records)
          </h3>
        </div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full">
            <thead className="bg-muted sticky top-0">
              <tr>
                {reportType === 'sales' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Transaction ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Payment Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  </>
                )}
                {reportType === 'attendance' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Member</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Check-in</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Check-out</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Duration</th>
                  </>
                )}
                {reportType === 'revenue' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Invoice #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  </>
                )}
                {reportType === 'membership' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Member</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Start Date</th>
                  </>
                )}
                {reportType === 'reconciliation' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Transaction ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Difference</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {reportData?.slice(0, 50)?.map((row, index) => (
                <tr key={index} className="hover:bg-muted/50">
                  {reportType === 'sales' && (
                    <>
                      <td className="px-4 py-3 text-sm text-foreground">{format(new Date(row?.transaction_date), 'MMM dd, yyyy')}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{row?.id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground">${parseFloat(row?.total_amount || 0)?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{row?.payment_method}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">{row?.payment_status}</span></td>
                    </>
                  )}
                  {reportType === 'attendance' && (
                    <>
                      <td className="px-4 py-3 text-sm text-foreground">{format(new Date(row?.check_in_time), 'MMM dd, yyyy')}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{row?.user_profiles?.full_name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{format(new Date(row?.check_in_time), 'HH:mm')}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{row?.check_out_time ? format(new Date(row?.check_out_time), 'HH:mm') : '-'}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{row?.duration_minutes ? `${row?.duration_minutes} min` : '-'}</td>
                    </>
                  )}
                  {reportType === 'revenue' && (
                    <>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{row?.invoice_number || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{format(new Date(row?.billing_date), 'MMM dd, yyyy')}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground">${parseFloat(row?.total_amount || 0)?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">{row?.payment_status}</span></td>
                    </>
                  )}
                  {reportType === 'membership' && (
                    <>
                      <td className="px-4 py-3 text-sm text-foreground">{row?.user_profiles?.full_name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{row?.membership_plans?.plan_name}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground">${parseFloat(row?.final_price || 0)?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">{row?.status}</span></td>
                      <td className="px-4 py-3 text-sm text-foreground">{format(new Date(row?.start_date), 'MMM dd, yyyy')}</td>
                    </>
                  )}
                  {reportType === 'reconciliation' && (
                    <>
                      <td className="px-4 py-3 text-sm text-foreground">{format(new Date(row?.transaction_date), 'MMM dd, yyyy')}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{row?.transaction_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground">${parseFloat(row?.expected_amount || 0)?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-red-600">${Math.abs(parseFloat(row?.amount_difference || 0))?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">{row?.reconciliation_status}</span></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {reportData?.length > 50 && (
          <div className="p-4 bg-muted text-center text-sm text-muted-foreground">
            Showing first 50 of {reportData?.length} records. Export to view all data.
          </div>
        )}
      </div>
    </div>
  );
};

export default DataVisualization;